import React, { useState, useEffect, useCallback } from 'react';
import { getMeals, createMeal, updateMeal, deleteMeal, addMealToList, getItems, createMealFromUrl } from '../api';
import { PlusIcon, TrashIcon, PencilIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { SkeletonList } from '../components/Skeleton';

const emptyMeal = { name: '', description: '', items: [] };

export default function Meals({ showToast }) {
  const [meals, setMeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editMeal, setEditMeal] = useState(null);
  const [isNew, setIsNew] = useState(false);
  const [allItems, setAllItems] = useState([]);
  const [itemSearch, setItemSearch] = useState('');
  const [addingToList, setAddingToList] = useState(null);
  const [recipeUrl, setRecipeUrl] = useState('');

  const loadMeals = useCallback(async () => {
    try {
      const data = await getMeals();
      setMeals(data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadMeals(); }, [loadMeals]);

  const handleAddRecipeUrl = async () => {
    if (!recipeUrl.trim()) { showToast('Please enter a URL'); return; }
    try {
      await createMealFromUrl(recipeUrl);
      setRecipeUrl('');
      loadMeals();
      showToast('Recipe added! It will be enriched shortly.');
    } catch (e) {
      showToast('Failed to add recipe');
    }
  };

  const openEditor = async (meal, creating) => {
    setIsNew(creating);
    if (creating) {
      setEditMeal({ ...emptyMeal });
    } else {
      setEditMeal({
        ...meal,
        items: meal.items.map(i => ({
          itemId: i.itemId?._id || i.itemId,
          friendlyName: i.itemId?.friendlyName || '(Unknown)',
          quantity: i.quantity
        }))
      });
    }
    try {
      const items = await getItems();
      setAllItems(items);
    } catch (e) { console.error(e); }
    setItemSearch('');
  };

  const handleSave = async () => {
    if (!editMeal.name.trim()) { showToast('Meal name is required'); return; }
    const payload = {
      name: editMeal.name,
      description: editMeal.description,
      items: editMeal.items.map(i => ({ itemId: i.itemId, quantity: i.quantity }))
    };
    try {
      if (isNew) {
        await createMeal(payload);
        showToast('Meal created');
      } else {
        await updateMeal(editMeal._id, payload);
        showToast('Meal updated');
      }
      setEditMeal(null);
      loadMeals();
    } catch (e) {
      showToast('Error saving meal');
    }
  };

  const handleDelete = async (id) => {
    await deleteMeal(id);
    loadMeals();
    showToast('Meal deleted');
  };

  const handleAddToList = async (meal) => {
    setAddingToList(meal._id);
    try {
      const res = await addMealToList(meal._id);
      showToast(`Added ${res.itemsAdded} item${res.itemsAdded !== 1 ? 's' : ''} from "${meal.name}" to shopping list`);
    } catch (e) {
      showToast('Error adding to list');
    } finally {
      setAddingToList(null);
    }
  };

  // Modal helpers
  const addItemToMeal = (item) => {
    if (editMeal.items.find(i => i.itemId === item._id)) return;
    setEditMeal({
      ...editMeal,
      items: [...editMeal.items, { itemId: item._id, friendlyName: item.friendlyName, quantity: 1 }]
    });
    setItemSearch('');
  };

  const updateMealItemQty = (itemId, delta) => {
    setEditMeal({
      ...editMeal,
      items: editMeal.items.map(i =>
        i.itemId === itemId ? { ...i, quantity: Math.max(1, i.quantity + delta) } : i
      )
    });
  };

  const removeMealItem = (itemId) => {
    setEditMeal({
      ...editMeal,
      items: editMeal.items.filter(i => i.itemId !== itemId)
    });
  };

  const filteredItems = allItems.filter(item =>
    item.friendlyName.toLowerCase().includes(itemSearch.toLowerCase()) &&
    !editMeal?.items.find(i => i.itemId === item._id)
  );

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold">Meals</h2>
        <button
          onClick={() => openEditor(null, true)}
          className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-xl flex items-center gap-2 font-medium"
        >
          <PlusIcon className="w-5 h-5" /> New Meal
        </button>
      </div>

      {/* Add recipe from URL */}
      <div className="flex gap-2 mb-4">
        <input
          type="url"
          value={recipeUrl}
          onChange={(e) => setRecipeUrl(e.target.value)}
          placeholder="Paste recipe URL..."
          className="flex-1 bg-gray-800 border border-gray-700 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500"
        />
        <button
          onClick={handleAddRecipeUrl}
          className="bg-green-600 hover:bg-green-700 text-white px-4 py-3 rounded-xl font-medium shrink-0"
        >
          Add Recipe
        </button>
      </div>

      {loading ? (
        <SkeletonList count={4} />
      ) : meals.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <p>No meals yet</p>
          <p className="text-sm mt-1">Create a meal to quickly add items to your shopping list</p>
        </div>
      ) : (
        <div className="space-y-3">
          {meals.map(meal => (
            <div key={meal._id} className="bg-gray-800 rounded-xl px-4 py-4 border border-gray-700">
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-lg">{meal.name}</h3>
                    {meal.enriched === false && (
                      <span className="text-xs bg-yellow-600/20 text-yellow-400 px-2 py-0.5 rounded">Pending</span>
                    )}
                  </div>
                  {meal.description && <p className="text-gray-400 text-sm mt-0.5">{meal.description}</p>}
                  {meal.url && (
                    <a href={meal.url} target="_blank" rel="noopener noreferrer" className="text-blue-400 text-xs hover:underline block mt-0.5 truncate">
                      {meal.url}
                    </a>
                  )}
                  <p className="text-gray-500 text-xs mt-1">
                    {meal.items.length} item{meal.items.length !== 1 ? 's' : ''}
                    {meal.items.length > 0 && (
                      <span> · {meal.items.map(i => i.itemId?.friendlyName || '?').join(', ')}</span>
                    )}
                  </p>
                </div>
                <div className="flex gap-2 ml-3 shrink-0">
                  <button
                    onClick={() => openEditor(meal, false)}
                    className="p-2 text-gray-400 hover:text-white"
                  >
                    <PencilIcon className="w-5 h-5" />
                  </button>
                  <button onClick={() => handleDelete(meal._id)} className="p-2 text-gray-400 hover:text-red-400">
                    <TrashIcon className="w-5 h-5" />
                  </button>
                </div>
              </div>
              <button
                onClick={() => handleAddToList(meal)}
                disabled={addingToList === meal._id || meal.items.length === 0}
                className="mt-3 w-full bg-green-600 hover:bg-green-700 disabled:bg-gray-600 disabled:text-gray-400 text-white py-2.5 rounded-xl font-medium text-sm transition-colors"
              >
                {addingToList === meal._id ? 'Adding...' : 'Add to Shopping List'}
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Edit/Create modal */}
      {editMeal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={() => setEditMeal(null)}>
          <div className="bg-gray-800 rounded-2xl p-6 w-full max-w-md border border-gray-700 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-bold mb-4">{isNew ? 'New Meal' : 'Edit Meal'}</h3>
            <div className="space-y-3">
              <div>
                <label className="text-sm text-gray-400 block mb-1">Meal Name *</label>
                <input
                  type="text" value={editMeal.name}
                  onChange={e => setEditMeal({ ...editMeal, name: e.target.value })}
                  className="w-full bg-gray-700 border border-gray-600 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                  placeholder="e.g. Tacos"
                  autoFocus
                />
              </div>
              <div>
                <label className="text-sm text-gray-400 block mb-1">Description</label>
                <input
                  type="text" value={editMeal.description}
                  onChange={e => setEditMeal({ ...editMeal, description: e.target.value })}
                  className="w-full bg-gray-700 border border-gray-600 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                  placeholder="e.g. Tuesday taco night"
                />
              </div>

              {/* Items in this meal */}
              <div>
                <label className="text-sm text-gray-400 block mb-2">Items ({editMeal.items.length})</label>
                {editMeal.items.length > 0 && (
                  <div className="space-y-2 mb-3">
                    {editMeal.items.map(item => (
                      <div key={item.itemId} className="bg-gray-700 rounded-xl px-3 py-2 flex items-center justify-between border border-gray-600">
                        <span className="text-sm font-medium truncate flex-1">{item.friendlyName}</span>
                        <div className="flex items-center gap-1 ml-2 shrink-0">
                          <button
                            type="button"
                            onClick={() => updateMealItemQty(item.itemId, -1)}
                            className="w-8 h-8 flex items-center justify-center bg-gray-600 hover:bg-gray-500 rounded-lg text-white text-sm font-bold"
                          >
                            −
                          </button>
                          <span className="w-8 text-center text-sm">{item.quantity}</span>
                          <button
                            type="button"
                            onClick={() => updateMealItemQty(item.itemId, 1)}
                            className="w-8 h-8 flex items-center justify-center bg-gray-600 hover:bg-gray-500 rounded-lg text-white text-sm font-bold"
                          >
                            +
                          </button>
                          <button
                            onClick={() => removeMealItem(item.itemId)}
                            className="ml-1 p-1 text-gray-400 hover:text-red-400"
                          >
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Item search/picker */}
                <div className="relative">
                  <MagnifyingGlassIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    value={itemSearch}
                    onChange={e => setItemSearch(e.target.value)}
                    placeholder="Search items to add..."
                    className="w-full bg-gray-700 border border-gray-600 rounded-xl pl-9 pr-4 py-2.5 text-white text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                  />
                </div>
                {itemSearch && filteredItems.length > 0 && (
                  <div className="mt-1 bg-gray-700 border border-gray-600 rounded-xl max-h-40 overflow-y-auto">
                    {filteredItems.slice(0, 10).map(item => (
                      <button
                        key={item._id}
                        onClick={() => addItemToMeal(item)}
                        className="w-full text-left px-4 py-2.5 hover:bg-gray-600 text-sm first:rounded-t-xl last:rounded-b-xl"
                      >
                        {item.friendlyName}
                        {item.variant && <span className="text-gray-400 ml-2">({item.variant})</span>}
                      </button>
                    ))}
                  </div>
                )}
                {itemSearch && filteredItems.length === 0 && (
                  <p className="text-gray-500 text-xs mt-2">No matching items found</p>
                )}
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={() => setEditMeal(null)} className="flex-1 bg-gray-700 hover:bg-gray-600 py-3 rounded-xl font-medium">
                Cancel
              </button>
              <button onClick={handleSave} className="flex-1 bg-red-600 hover:bg-red-700 py-3 rounded-xl font-semibold">
                {isNew ? 'Create' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
